import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/auth';
import { ErrorBoundary } from './components/ErrorBoundary';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';
import AuthLayout from './layouts/AuthLayout';

// Pages
import Home from './pages/Home';
import Login from './pages/Login';
import Register from './pages/Register';
import AuthCallback from './pages/AuthCallback';
import Onboarding from './pages/Onboarding';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ProjectDetail from './pages/ProjectDetail';
import ProjectSettings from './pages/ProjectSettings';
import SEOModule from './pages/modules/SEO';
import SocialModule from './pages/modules/Social';
import AdsModule from './pages/modules/Ads';
import EmailModule from './pages/modules/Email';
import PaymentsModule from './pages/modules/Payments';
import AnalyticsModule from './pages/modules/Analytics';
import Settings from './pages/Settings';
import Billing from './pages/settings/Billing';

// Product Pages
import MarketingAutomation from './pages/products/MarketingAutomation';
import DevAI from './pages/products/DevAI';
import BizOps from './pages/products/BizOps';
import Pricing from './pages/Pricing';
import UsageDashboard from './pages/UsageDashboard';

// Protected Route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const hasCompletedOnboarding = useAuthStore((state) => state.hasCompletedOnboarding);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // Redirect new users to onboarding if they haven't completed it
    if (!hasCompletedOnboarding && window.location.pathname !== '/onboarding') {
        return <Navigate to="/onboarding" replace />;
    }

    return <>{children}</>;
}

// Onboarding Route wrapper - allows skipping if already completed
function OnboardingRoute({ children }: { children: React.ReactNode }) {
    const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
    const hasCompletedOnboarding = useAuthStore((state) => state.hasCompletedOnboarding);

    if (!isAuthenticated) {
        return <Navigate to="/login" replace />;
    }

    // If onboarding already completed, redirect to dashboard
    if (hasCompletedOnboarding) {
        return <Navigate to="/dashboard" replace />;
    }

    return <>{children}</>;
}

export default function App() {
    return (
        <ErrorBoundary>
            <Routes>
                {/* Public home page */}
                <Route path="/" element={<Home />} />

                {/* Product pages */}
                <Route path="/products/marketing-automation" element={<MarketingAutomation />} />
                <Route path="/products/devai" element={<DevAI />} />
                <Route path="/products/bizops" element={<BizOps />} />
                <Route path="/pricing" element={<Pricing />} />

                {/* Auth routes */}
                <Route element={<AuthLayout />}>
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                </Route>

                {/* OAuth callback (no layout) */}
                <Route path="/auth/callback" element={<AuthCallback />} />

                {/* Onboarding (protected, shows only for new users) */}
                <Route
                    path="/onboarding"
                    element={
                        <OnboardingRoute>
                            <Onboarding />
                        </OnboardingRoute>
                    }
                />

                {/* Protected routes */}
                <Route
                    element={
                        <ProtectedRoute>
                            <DashboardLayout />
                        </ProtectedRoute>
                    }
                >
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/projects" element={<Projects />} />
                    <Route path="/projects/:projectId" element={<ProjectDetail />} />
                    <Route path="/projects/:projectId/settings" element={<ProjectSettings />} />
                    <Route path="/projects/:projectId/seo" element={<SEOModule />} />
                    <Route path="/projects/:projectId/social" element={<SocialModule />} />
                    <Route path="/projects/:projectId/ads" element={<AdsModule />} />
                    <Route path="/projects/:projectId/email" element={<EmailModule />} />
                    <Route path="/projects/:projectId/payments" element={<PaymentsModule />} />
                    <Route path="/projects/:projectId/analytics" element={<AnalyticsModule />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/settings/billing" element={<Billing />} />
                    <Route path="/usage" element={<UsageDashboard />} />
                </Route>

                {/* Catch all */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </ErrorBoundary>
    );
}
